/**
 * 48. Purchase Orders — list; editor; delivery schedule; approval
 * matrix; send to supplier; receive; split-purchase detection.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  BlockedActionExplanation,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatMoney, formatDate } from "@/lib/money";

interface PurchaseOrderRow {
  id: string;
  orderNumber: string;
  supplierName: string;
  orderDate: string;
  expectedDate: string | null;
  total: string;
  currencyCode: string;
  status:
    | "Draft"
    | "Approved"
    | "Sent"
    | "Confirmed"
    | "PartiallyReceived"
    | "Received"
    | "Closed"
    | "Cancelled";
  splitPurchaseFlagged: boolean;
}

export function PurchaseOrdersPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<PurchaseOrderRow>(
    ["procurement", "purchase-orders"],
    "/procurement/purchase-orders",
  );

  const flagged = (data?.items ?? []).filter((r) => r.splitPurchaseFlagged);

  return (
    <div>
      <PageHeader
        title="Purchase Orders"
        subtitle="Procure-to-pay: approval matrix, send to supplier, receive, close."
        actions={
          <PendingButton
            variant="primary"
            reason="purchaseEditor"
            slice="ED-W3"
          >
            {t("documentActions.newPurchaseOrder")}
          </PendingButton>
        }
      />
      {flagged.length > 0 && (
        <BlockedActionExplanation
          title="Split-purchase detection"
          reasons={flagged.map((r) => ({
            code: "SPLIT_PURCHASE_DETECTED",
            message: `PO ${r.orderNumber} may split a purchase to avoid an approval threshold (same supplier, same day).`,
            resolutionPath:
              "Review the flagged orders and merge or document the split.",
          }))}
        />
      )}
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Purchase orders (${data.total})`}>
          <DataTable<PurchaseOrderRow>
            rows={data.items}
            columns={[
              {
                key: "orderNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.orderNumber}</span>
                ),
              },
              { key: "supplierName", label: "Supplier" },
              {
                key: "orderDate",
                label: "Date",
                render: (r) => formatDate(r.orderDate),
              },
              {
                key: "expectedDate",
                label: "Expected",
                render: (r) =>
                  r.expectedDate ? formatDate(r.expectedDate) : "—",
              },
              {
                key: "total",
                label: "Total",
                numeric: true,
                render: (r) => formatMoney(r.total, r.currencyCode),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
